import { Container, Sprite, TextStyle, Ticker } from "pixi.js";
import { LoseResourcesPackage } from "../resources/LoseResourcesPackage";
import { LocalizationModel } from "../../../../common/models/LocalizationModel";
import { LocalizationKey } from "../../../../localization/Localization";
import { GameFont } from "../../../../common/GameFont";
import { LoseType } from "../LoseType";
import { TypedText } from "../../../../common/components/TypedText";
import { CancellationToken } from "../../../../../composition/CancellationToken";

export class LoseReasonView extends Container {
    public readonly cancellation: CancellationToken;

    private readonly panel: Sprite;
    private readonly icon: Sprite;
    private readonly text: TypedText;

    public constructor(
        loseResources: LoseResourcesPackage,
        private readonly localizationModel: LocalizationModel,
        loseType: LoseType
    ) {
        super();
        this.cancellation = new CancellationToken();
        this.panel = new Sprite(loseResources.assets_popups_lose_reason_substrate_png);
        this.panel.anchor.set(0.5);
        this.panel.scale.y = 0.1;
        this.panel.alpha = 0;

        let iconTexture = loseResources.assets_popups_lose_reason_vegetable_icon_png;
        let localizationKey = LocalizationKey.NoVegetablesLeft;
        let textYOffset = 0;

        if (loseType === LoseType.Dead) {
            iconTexture = loseResources.assets_popups_lose_reason_mole_icon_png;
            localizationKey = LocalizationKey.MolesWon;
            textYOffset = -5;
        }

        this.icon = new Sprite(iconTexture);
        this.icon.anchor.set(0.5);
        this.icon.position.set(this.width * 0.5, this.height * 0.5);
        this.icon.scale.set(0);

        this.text = new TypedText(this.localizationModel, localizationKey, new LoseReasonTextStyle(), 380, 60, -190.358);
        this.text.anchor.set(0, 0.5);
        this.text.position.y += textYOffset;

        this.panel.addChild(this.text);
        this.addChild(this.panel, this.icon);
        this.text.updateText(false);
    }

    public async animate(): Promise<void> {
        return new Promise((resolve) => {
            if (this.cancellation.isCancellationRequested) {
                resolve();
                return;
            }
            // icon starts at top-center
            const startIconPos = this.icon.position.clone();
            const finalIconPos = { x: -220, y: 0 };
            const finalIconScale = 1;

            const panelStartScaleY = 0.1;
            this.panel.scale.y = panelStartScaleY;

            let elapsed = 0;
            const phase1Duration = 200;  // icon scale-up
            const phase2Duration = 250;  // move icon + expand panel

            const onTick = () => {
                if (this.cancellation.isCancellationRequested) {
                    Ticker.shared.remove(onTick, this);
                    resolve();
                    return;
                }
                elapsed += Ticker.shared.elapsedMS;

                if (elapsed <= phase1Duration) {
                    // Phase 1: icon scale-up
                    const t = Math.min(elapsed / phase1Duration, 1);
                    const scale = 0.1 + (finalIconScale - 0.1) * t;
                    this.icon.scale.set(scale);
                } else if (elapsed <= phase1Duration + phase2Duration) {
                    this.icon.scale.set(1);
                    // Phase 2: move icon & expand panel in parallel
                    const t = Math.min((elapsed - phase1Duration) / phase2Duration, 1);

                    // icon move
                    this.icon.position.set(startIconPos.x + (finalIconPos.x - startIconPos.x) * t, startIconPos.y + (finalIconPos.y - startIconPos.y) * t);

                    // panel expand
                    this.panel.scale.y = panelStartScaleY + (1 - panelStartScaleY) * t;
                    this.panel.alpha = t;
                } else {
                    this.panel.scale.y = 1;
                    this.panel.alpha = 1;
                    // Done with animation
                    Ticker.shared.remove(onTick, this);

                    // start text typing
                    this.text.animateText().then(resolve);
                }
            };

            Ticker.shared.add(onTick, this);
        });
    }
}

class LoseReasonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 32,
            fill: "#FFFFFF",
            fontFamily: GameFont.Default,
            dropShadow: true,
            dropShadowColor: "#2B008C",
            dropShadowBlur: 0.5,
            dropShadowDistance: 4,
            dropShadowAngle: 1.77
        });
    }
}
